// ── Settings ──────────────────────────────────────────────────────────
const EXCHANGES = [
  { id:"kraken",   name:"Kraken",             logo:"K",  color:"#D946EF", connected:false, balance:null },
  { id:"binance",  name:"Binance",            logo:"B",  color:"#3b9eff", connected:false, balance:null },
  { id:"okx",      name:"OKX",                logo:"O",  color:"#60a5fa", connected:false, balance:null },
  { id:"bybit",    name:"Bybit",              logo:"BB", color:"#11ff99", connected:false, balance:null },
  { id:"coinbase", name:"Coinbase Advanced",  logo:"C",  color:"#11ff99", connected:false, balance:null },
  { id:"ibkr",     name:"Interactive Brokers",logo:"IB", color:"#fb7185", connected:false, balance:null },
];

const PLANS = [
  { id:"starter", name:"Trial",    price:"$0",   period:"/14 days", features:["3 strategies","Basic backtesting","Community access"], current:false },
  { id:"pro",     name:"Members",  price:"$249", period:"/mo",      features:["Unlimited strategies","GQ™ Optimize","Stress Test","Marketplace","PDF tearsheets"], current:true },
  { id:"elite",   name:"Elite",    price:"$749", period:"/mo",      features:["Everything in Members","Priority GQ™ Optimize","White-glove onboarding","API access","SLA"], current:false },
];

function Toggle({ on, onChange }) {
  return (
    <button onClick={()=>onChange(!on)} style={{
      width:36, height:20, borderRadius:"var(--r-full)", border:"none", cursor:"pointer", padding:0, flexShrink:0,
      background: on?"var(--primary)":"rgba(255,255,255,0.1)", transition:"background .2s", position:"relative",
    }}>
      <div style={{ width:14, height:14, borderRadius:"50%", background:"#fff", position:"absolute", top:3, left:on?19:3, transition:"left .2s" }}/>
    </button>
  );
}

function SettingsPage({ onToast, initialTab }) {
  const narrow = window.useNarrow ? window.useNarrow() : false;
  const [tab, setTab]             = React.useState(initialTab || "profile");
  const [notifs, setNotifs]       = React.useState({ tradeAlerts:true, optimizeComplete:true, newSubscriber:true, weeklyReport:false, marketNews:false, productUpdates:true });
  const [exchanges, setExchanges] = React.useState(EXCHANGES);
  const [profileSaving, setProfileSaving] = React.useState(false);
  const [pwSaving, setPwSaving] = React.useState(false);
  const [pwError, setPwError] = React.useState("");
  const [betaFeatures, setBetaFeatures] = React.useState({});
  const [userEmail, setUserEmail] = React.useState("");
  const [apiKey, setApiKey] = React.useState({ prefix: "", id: null, fresh: null });
  const [apiKeyGenerating, setApiKeyGenerating] = React.useState(false);
  const [apiKeyModal, setApiKeyModal] = React.useState(null); // { key } when newly created
  const [exchangeRequested, setExchangeRequested] = React.useState(false);
  const profileRefs = { firstName: React.useRef(), lastName: React.useRef(), email: React.useRef(), username: React.useRef(), bio: React.useRef() };
  const pwRefs = { current: React.useRef(), next: React.useRef(), confirm: React.useRef() };

  // Load profile beta_features and existing API key prefix on mount
  React.useEffect(() => {
    (async () => {
      try {
        const sb = window.sbClient;
        const { data: { user } } = await sb.auth.getUser();
        if (!user) return;
        setUserEmail(user.email || "");
        const { data: prof } = await sb.from('profiles').select('beta_features').eq('id', user.id).maybeSingle();
        setBetaFeatures(prof?.beta_features || {});
        const { data: keys } = await sb.from('user_api_keys')
          .select('id, key_prefix, created_at')
          .eq('user_id', user.id).is('revoked_at', null)
          .order('created_at', { ascending: false }).limit(1);
        if (keys?.[0]) setApiKey({ prefix: keys[0].key_prefix, id: keys[0].id, fresh: null });
      } catch (_) { /* non-fatal */ }
    })();
  }, []);

  const TABS = ["profile","exchanges","data","subscription","notifications"];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <PageHdr icon={Settings} iconColor="var(--muted)" iconBg="rgba(255,255,255,0.05)"
        title="Settings"
        subtitle="Profile, exchange connections, and billing"
      />

      <div style={{ display:"flex", gap:0, borderBottom:"1px solid var(--border)" }}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{
            background:"none", border:"none", borderBottom:`2px solid ${tab===t?"var(--primary)":"transparent"}`,
            padding:"8px 18px", cursor:"pointer",
            fontFamily:"var(--font-ui)", fontSize:13, fontWeight: tab===t?600:400,
            color: tab===t?"var(--primary)":"var(--muted)", textTransform:"capitalize",
          }}>{t}</button>
        ))}
      </div>

      {tab === "profile" && (
        <div style={{ display:"grid", gridTemplateColumns: narrow ? "1fr" : "1fr 1fr", gap:12 }}>
          <div className="card">
            <div className="card-title">Personal information</div>
            <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:20 }}>
              <div style={{ width:64, height:64, borderRadius:"50%", background:"var(--primary-dim)", border:"1px solid rgba(217,70,239,0.3)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"var(--font-ui)", fontSize:22, fontWeight:600, color:"var(--primary)" }}>YO</div>
              <div>
                <button className="btn btn-ghost btn-sm" onClick={()=>onToast?.("Upload avatar…")}><Upload size={11}/> Change photo</button>
                <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--subtle)", marginTop:6 }}>JPG or PNG, max 2MB</div>
              </div>
            </div>
            {[
              {l:"First name", r:profileRefs.firstName, def:""},
              {l:"Last name",  r:profileRefs.lastName,  def:""},
              {l:"Email",      r:profileRefs.email,     def:""},
              {l:"Username",   r:profileRefs.username,  def:""},
              {l:"Bio",        r:profileRefs.bio,       def:"", multi:true},
            ].map(({l,r,def,multi})=>(
              <div key={l} style={{ marginBottom:12 }}>
                <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)", marginBottom:5 }}>{l}</div>
                {multi
                  ? <textarea ref={r} defaultValue={def} rows={3} className="input" style={{ resize:"vertical" }}/>
                  : <input ref={r} defaultValue={def} className="input"/>
                }
              </div>
            ))}
            <button className="btn btn-primary btn-sm" disabled={profileSaving} style={{ marginTop:4, opacity:profileSaving?0.7:1 }} onClick={async () => {
              setProfileSaving(true);
              try {
                const sb = window.sbClient;
                const updates = {};
                const email = profileRefs.email.current?.value?.trim();
                const meta = {
                  first_name: profileRefs.firstName.current?.value?.trim(),
                  last_name:  profileRefs.lastName.current?.value?.trim(),
                  username:   profileRefs.username.current?.value?.trim(),
                  bio:        profileRefs.bio.current?.value?.trim(),
                };
                if (email) updates.email = email;
                updates.data = meta;
                const { error } = await sb.auth.updateUser(updates);
                if (error) throw error;
                onToast?.("Profile saved");
              } catch (e) { onToast?.(`Save failed: ${e.message}`); }
              setProfileSaving(false);
            }}><Check size={11}/> {profileSaving ? "Saving…" : "Save changes"}</button>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <div className="card">
              <div className="card-title">Security</div>
              {[["Current password", pwRefs.current],["New password", pwRefs.next],["Confirm password", pwRefs.confirm]].map(([l,r])=>(
                <div key={l} style={{ marginBottom:10 }}>
                  <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)", marginBottom:5 }}>{l}</div>
                  <input ref={r} type="password" placeholder="••••••••" className="input"/>
                </div>
              ))}
              {pwError && (
                <div style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--red)", marginBottom:8 }}>{pwError}</div>
              )}
              <button className="btn btn-ghost btn-sm" disabled={pwSaving} style={{ marginTop:4, opacity:pwSaving?0.7:1 }} onClick={async () => {
                setPwError("");
                const cur = pwRefs.current.current?.value;
                const next = pwRefs.next.current?.value;
                const conf = pwRefs.confirm.current?.value;
                if (!cur) { setPwError("Enter your current password."); return; }
                if (!next || next.length < 8) { setPwError("New password must be at least 8 characters."); return; }
                if (next !== conf) { setPwError("New passwords don't match."); return; }
                if (next === cur) { setPwError("New password must differ from current."); return; }
                setPwSaving(true);
                try {
                  // Verify current password by re-authenticating. Supabase's signInWithPassword
                  // does not invalidate the active session on success.
                  const sb = window.sbClient;
                  const email = userEmail || (await sb.auth.getUser()).data?.user?.email;
                  if (!email) throw new Error("Couldn't read your account email.");
                  const { error: reauthErr } = await sb.auth.signInWithPassword({ email, password: cur });
                  if (reauthErr) { setPwError("Current password is incorrect."); setPwSaving(false); return; }
                  const { error } = await sb.auth.updateUser({ password: next });
                  if (error) throw error;
                  onToast?.("Password updated successfully");
                  if (pwRefs.current.current)  pwRefs.current.current.value = "";
                  if (pwRefs.next.current)     pwRefs.next.current.value = "";
                  if (pwRefs.confirm.current)  pwRefs.confirm.current.value = "";
                } catch (e) { setPwError(`Password update failed: ${e.message}`); }
                setPwSaving(false);
              }}>{pwSaving ? "Updating…" : "Update password"}</button>
            </div>
            <div className="card">
              <div className="card-title">API access</div>
              <div style={{ background:"var(--bg-deep)", border:"1px solid var(--border)", borderRadius:"var(--r-md)", padding:"10px 12px", marginBottom:10 }}>
                <div style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)", marginBottom:4 }}>
                  {apiKey.prefix ? "Active API key (prefix only — full key shown once at creation)" : "No API key yet"}
                </div>
                <div style={{ fontFamily:"var(--font-mono)", fontSize:11, color: apiKey.prefix ? "var(--muted)" : "var(--subtle)", letterSpacing:"0.04em" }}>
                  {apiKey.prefix ? `${apiKey.prefix}${"•".repeat(16)}` : "—"}
                </div>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                <button className="btn btn-ghost btn-sm" disabled={!apiKey.prefix} onClick={async () => {
                  if (!apiKey.prefix) return;
                  try {
                    if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(apiKey.prefix);
                    onToast?.("Prefix copied. Full key is only shown at creation.");
                  } catch (e) {
                    onToast?.("Couldn't access clipboard.");
                  }
                }}>Copy prefix</button>
                <button className="btn btn-ghost btn-sm" disabled={apiKeyGenerating} onClick={async () => {
                  if (apiKey.prefix && !window.confirm("Regenerating revokes the current key. Continue?")) return;
                  setApiKeyGenerating(true);
                  try {
                    const sb = window.sbClient;
                    const { data: { session } } = await sb.auth.getSession();
                    if (!session?.access_token) throw new Error("Not signed in.");
                    const resp = await fetch('/api/keys/generate', {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                      body: '{}',
                    });
                    const data = await resp.json().catch(() => ({}));
                    if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
                    setApiKey({ prefix: data.prefix, id: data.id, fresh: data.key });
                    setApiKeyModal({ key: data.key });
                  } catch (e) {
                    onToast?.(`Key generation failed: ${e.message}`);
                  }
                  setApiKeyGenerating(false);
                }}>
                  {apiKeyGenerating
                    ? <><RefreshCw size={11} style={{animation:"spin 1s linear infinite"}}/> Generating…</>
                    : <><RefreshCw size={11}/> {apiKey.prefix ? "Regenerate" : "Generate"}</>}
                </button>
              </div>
              {apiKeyModal && (
                <div style={{
                  position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9999,
                  display:"flex", alignItems:"center", justifyContent:"center", padding:20,
                }}>
                  <div className="card" style={{ maxWidth:520, width:"100%", border:"1px solid rgba(251,191,36,0.4)" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                      <AlertTriangle size={16} color="var(--yellow)"/>
                      <div style={{ fontFamily:"var(--font-ui)", fontSize:14, fontWeight:600, color:"var(--ink)" }}>
                        Copy your API key now
                      </div>
                    </div>
                    <div style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--body)", marginBottom:12, lineHeight:1.5 }}>
                      This is the only time the full key will be shown. If you lose it, regenerate to get a new one — the old one stops working.
                    </div>
                    <div style={{
                      background:"var(--bg-deep)", border:"1px solid var(--border-strong)",
                      borderRadius:"var(--r-md)", padding:"10px 12px", marginBottom:14,
                      fontFamily:"var(--font-mono)", fontSize:12, color:"var(--ink)",
                      wordBreak:"break-all",
                    }}>{apiKeyModal.key}</div>
                    <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                      <button className="btn btn-ghost btn-sm" onClick={async () => {
                        try {
                          if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(apiKeyModal.key);
                          onToast?.("Key copied to clipboard");
                        } catch (e) { onToast?.("Couldn't copy."); }
                      }}>Copy</button>
                      <button className="btn btn-primary btn-sm" onClick={() => setApiKeyModal(null)}>I've saved it</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "data" && <DataUploadTab onToast={onToast}/>}

      {tab === "exchanges" && (
        betaFeatures?.exchanges ? (
          <>
          <div style={{
            background:"rgba(217,70,239,0.06)", border:"1px solid rgba(217,70,239,0.2)",
            borderRadius:"var(--r-md)", padding:"10px 14px", marginBottom:16,
            fontFamily:"var(--font-ui)", fontSize:12, color:"rgba(217,70,239,0.9)",
            display:"flex", alignItems:"center", gap:10,
          }}>
            <Info size={13}/> Beta access enabled — connections are stored encrypted and used for read-only balance checks. Paper trading remains the default execution mode.
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:12 }}>
            {exchanges.map(ex=>(
              <div key={ex.id} className="card" style={{ border:`1px solid ${ex.connected?"rgba(17,255,153,0.18)":"var(--border)"}` }}>
                <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
                  <div style={{ width:40, height:40, borderRadius:"var(--r-md)", background:`${ex.color}18`, border:`1px solid ${ex.color}40`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"var(--font-ui)", fontSize:12, fontWeight:700, color:ex.color }}>{ex.logo}</div>
                  <div>
                    <div style={{ fontFamily:"var(--font-ui)", fontSize:13, fontWeight:600, color:"var(--ink)" }}>{ex.name}</div>
                    <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color: ex.connected?"var(--green)":"var(--muted)", marginTop:2 }}>{ex.connected?"● Connected":"Not connected"}</div>
                  </div>
                </div>
                {ex.connected
                  ? <button className="btn btn-danger btn-sm" style={{ width:"100%", justifyContent:"center" }} onClick={()=>onToast?.("Disconnect flow ships in v1.1")}>Disconnect</button>
                  : <button className="btn btn-primary btn-sm" style={{ width:"100%", justifyContent:"center" }} onClick={()=>onToast?.(`${ex.name} OAuth ships in v1.1`)}>Connect {ex.name}</button>
                }
              </div>
            ))}
          </div>
          </>
        ) : (
          <div className="card" style={{
            border:"1px solid rgba(59,158,255,0.25)",
            background:"linear-gradient(180deg, rgba(59,158,255,0.04) 0%, transparent 100%)",
            padding:32, textAlign:"center", maxWidth:560, margin:"0 auto",
          }}>
            <div style={{ width:54, height:54, borderRadius:"var(--r-lg)", background:"rgba(59,158,255,0.12)", border:"1px solid rgba(59,158,255,0.3)", display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:18 }}>
              <Wallet size={22} color="#3b9eff"/>
            </div>
            <div style={{ fontFamily:"var(--font-display)", fontSize:22, fontWeight:600, color:"var(--ink)", letterSpacing:"-0.02em", marginBottom:6 }}>
              Live exchange connections — coming Q3
            </div>
            <div style={{ fontFamily:"var(--font-ui)", fontSize:13, color:"var(--body)", lineHeight:1.55, marginBottom:18 }}>
              The alpha runs all strategies in paper-trading mode. Live execution on Kraken, Binance, Coinbase, IBKR and others is on the roadmap — request early access and we'll prioritise the venues our founding members ask for.
            </div>
            <button
              className="btn btn-primary btn-sm"
              disabled={exchangeRequested}
              onClick={async () => {
                if (exchangeRequested) return;
                try {
                  const sb = window.sbClient;
                  const { data: { user } } = await sb.auth.getUser();
                  if (!user) throw new Error("Not signed in");
                  await sb.from('feedback').insert({
                    user_id: user.id,
                    type: 'Feature request',
                    body: 'Exchange connections — request early access',
                    page: 'settings/exchanges',
                  });
                  setExchangeRequested(true);
                  onToast?.("Thanks — we'll be in touch when live exchanges are ready");
                } catch (e) { onToast?.(`Couldn't submit request: ${e.message}`); }
              }}
            >
              {exchangeRequested ? <><Check size={11}/> Requested — we'll be in touch</> : "Request early access"}
            </button>
          </div>
        )
      )}

      {tab === "subscription" && (
        <div>
          <div style={{
            background:"rgba(217,70,239,0.06)", border:"1px solid rgba(217,70,239,0.2)",
            borderRadius:"var(--r-md)", padding:"12px 16px", marginBottom:20,
            fontFamily:"var(--font-ui)", fontSize:13, color:"rgba(245,245,247,0.75)", lineHeight:1.6,
          }}>
            <strong style={{color:"#D946EF"}}>Private alpha — billing not active.</strong>{" "}
            All features are free during the invite-only alpha. Paid plans will launch after the alpha period. Your early access entitles you to a founding-member discount.
          </div>
          <div style={{ display:"grid", gridTemplateColumns: narrow ? "1fr" : "repeat(3,1fr)", gap:12 }}>
            {PLANS.map(plan=>(
              <div key={plan.id} className="card" style={{ position:"relative", border:plan.current?"1px solid var(--border-strong)":"1px solid var(--border)" }}>
                {plan.current && <div style={{ position:"absolute", top:-10, left:"50%", transform:"translateX(-50%)", background:"var(--ink)", color:"#0a0a0a", fontFamily:"var(--font-ui)", fontSize:10, fontWeight:600, borderRadius:"var(--r-full)", padding:"3px 10px" }}>Current plan</div>}
                <div style={{ fontFamily:"var(--font-display)", fontSize:22, fontWeight:600, color:"var(--ink)", marginBottom:4, letterSpacing:"-0.02em" }}>{plan.name}</div>
                <div style={{ display:"flex", alignItems:"baseline", gap:3, marginBottom:16 }}>
                  <span style={{ fontFamily:"var(--font-display)", fontSize:30, color:"var(--ink)", fontWeight:600, letterSpacing:"-0.025em" }}>{plan.price}</span>
                  <span style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--muted)" }}>{plan.period}</span>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:20 }}>
                  {plan.features.map(f=>(
                    <div key={f} style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <Check size={11} color="var(--primary)"/>
                      <span style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--body)" }}>{f}</span>
                    </div>
                  ))}
                </div>
                {plan.current
                  ? <button className="btn btn-ghost btn-sm" style={{ width:"100%", justifyContent:"center" }} onClick={()=>onToast?.("Manage subscription…")}>Manage</button>
                  : <button className="btn btn-primary btn-sm" style={{ width:"100%", justifyContent:"center" }} onClick={()=>onToast?.(`Switching to ${plan.name}…`)}>{plan.id==="starter"?"Downgrade":"Upgrade"}</button>
                }
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "notifications" && (
        <div className="card" style={{ maxWidth:520 }}>
          <div className="card-title">Notification preferences</div>
          {[
            { key:"tradeAlerts",      label:"Trade alerts",          desc:"When a strategy opens or closes a position" },
            { key:"optimizeComplete", label:"Optimization complete", desc:"When GQ™ Optimize finishes a run" },
            { key:"newSubscriber",    label:"New subscriber",        desc:"When someone licenses your strategy" },
            { key:"weeklyReport",     label:"Weekly report",         desc:"Portfolio performance summary" },
            { key:"marketNews",       label:"Market news",           desc:"Macro events that may affect strategies" },
            { key:"productUpdates",   label:"Product updates",       desc:"New features and improvements" },
          ].map(n=>(
            <div key={n.key} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 0", borderBottom:"1px solid var(--border)" }}>
              <div>
                <div style={{ fontFamily:"var(--font-ui)", fontSize:13, color:"var(--ink)", fontWeight:500 }}>{n.label}</div>
                <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)", marginTop:2 }}>{n.desc}</div>
              </div>
              <Toggle on={notifs[n.key]} onChange={v=>{ setNotifs(p=>({...p,[n.key]:v})); onToast?.(`${n.label} ${v?"enabled":"disabled"}`); }}/>
            </div>
          ))}
          <button className="btn btn-primary btn-sm" onClick={()=>onToast?.("Saved")} style={{ marginTop:16 }}><Check size={11}/> Save</button>
        </div>
      )}
    </div>
  );
}
// ── CSV Upload Tab (Data) ─────────────────────────────────────────────
// Auto-detects format:
//   • OHLCV columns (open/high/low/close) → cached into window.GQ_DATA_CACHE
//   • TradingView trade-export columns (Date/Time, Trade #, P&L) → test_table
function DataUploadTab({ onToast }) {
  const narrow = window.useNarrow ? window.useNarrow() : false;
  const [dragging, setDragging] = React.useState(false);
  const [pasteText, setPasteText] = React.useState("");
  const [pasteSymbol, setPasteSymbol] = React.useState("");
  const [parsing, setParsing] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [cached, setCached] = React.useState(() => Object.keys(window.GQ_DATA_CACHE || {}));
  const fileInput = React.useRef(null);

  function detectFormat(headers) {
    const lower = headers.map(h => h.toLowerCase());
    const hasOHLCV = ['open','high','low','close'].every(k => lower.includes(k));
    const hasTimeOrDate = lower.includes('time') || lower.includes('date');
    const isTradingView = headers.some(h => /trade #|p&l/i.test(h));
    if (hasOHLCV && hasTimeOrDate) return 'ohlcv';
    if (isTradingView) return 'tradingview';
    return 'unknown';
  }

  async function parseAndUpload(csv, filename) {
    setParsing(true); setResult(null);
    try {
      const lines = csv.trim().split(/\r?\n/);
      if (lines.length < 2) throw new Error("CSV is empty or has no data rows");
      const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
      const format = detectFormat(headers);

      if (format === 'ohlcv') {
        // Parse via data.js helper which knows time/date column tolerance
        const bars = window.GQ.data.parseCSV(csv);
        // Symbol = filename (minus .csv) or pasteSymbol or "UPLOAD"
        const sym = (filename ? filename.replace(/\.csv$/i, '').toUpperCase()
                              : pasteSymbol.toUpperCase() || 'UPLOAD');
        window.GQ_DATA_CACHE = window.GQ_DATA_CACHE || {};
        window.GQ_DATA_CACHE[sym] = bars;
        setCached(Object.keys(window.GQ_DATA_CACHE));
        setResult({
          ok: true, count: bars.length, format: 'ohlcv', symbol: sym,
          range: `${bars[0].time} → ${bars[bars.length-1].time}`,
        });
        onToast?.(`✓ Cached ${bars.length} OHLCV bars as "${sym}"`);
      } else if (format === 'tradingview') {
        const rows = lines.slice(1).map(line => {
          const cells = line.split(",").map(c => c.trim().replace(/^"|"$/g, ""));
          const obj = {};
          headers.forEach((h, i) => { obj[h] = cells[i]; });
          return obj;
        });
        const { error } = await window.sbClient.from("test_table").insert(rows).select();
        if (error && error.code !== "23505") {
          setResult({ ok:false, count:0, err: error.message });
        } else {
          setResult({ ok:true, count:rows.length, format: 'tradingview' });
          onToast?.(`✓ Uploaded ${rows.length} trades to test_table`);
        }
      } else {
        setResult({
          ok: false, count: 0,
          err: `Unknown CSV format. Expected OHLCV (time, open, high, low, close, volume) or TradingView trade export (Date/Time, Trade #, P&L). Got headers: ${headers.slice(0, 6).join(', ')}…`,
        });
      }
    } catch (err) {
      setResult({ ok:false, count:0, err: err.message });
    }
    setParsing(false);
  }

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => parseAndUpload(ev.target.result, file.name);
    reader.readAsText(file);
  };

  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => parseAndUpload(ev.target.result, file.name);
    reader.readAsText(file);
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
      {/* Format guide */}
      <div className="card" style={{ background:"rgba(59,158,255,0.04)", border:"1px solid rgba(59,158,255,0.18)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
          <FileText size={14} color="#3b9eff"/>
          <span style={{ fontFamily:"var(--font-ui)", fontSize:13, fontWeight:600, color:"var(--ink)" }}>Two formats supported — auto-detected on upload</span>
        </div>
        <div style={{ display:"grid", gridTemplateColumns: narrow ? "1fr" : "1fr 1fr", gap:14, marginTop:4 }}>
          <div>
            <div style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"#11ff99", letterSpacing:".05em", marginBottom:4 }}>OHLCV (market data)</div>
            <div style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--body)", lineHeight:1.5 }}>
              Headers: <code style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--ink)"}}>time, open, high, low, close, volume</code>. Filename = symbol (e.g. <code style={{fontFamily:"var(--font-mono)",fontSize:11}}>AAPL.csv</code>). Cached for use in Backtest.
            </div>
          </div>
          <div>
            <div style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"#D946EF", letterSpacing:".05em", marginBottom:4 }}>TradingView trade export</div>
            <div style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--body)", lineHeight:1.5 }}>
              Headers: <code style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--ink)"}}>Date/Time, Trade #, Type, P&amp;L USD, …</code>. Upserted into <code style={{fontFamily:"var(--font-mono)",fontSize:11}}>test_table</code>. Powers the Tearsheet.
            </div>
          </div>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns: narrow ? "1fr" : "1fr 1fr", gap:12 }}>
        {/* Drag-drop zone */}
        <div className="card">
          <div className="card-title">Drop CSV file</div>
          <div
            onDragOver={e=>{ e.preventDefault(); setDragging(true); }}
            onDragLeave={()=>setDragging(false)}
            onDrop={onDrop}
            onClick={()=>fileInput.current?.click()}
            style={{
              border:`2px dashed ${dragging?"#11ff99":"var(--border-strong)"}`,
              background: dragging ? "rgba(17,255,153,0.06)" : "var(--bg-deep)",
              borderRadius:"var(--r-lg)", padding:"40px 20px", textAlign:"center", cursor:"pointer",
              transition:"all .15s",
            }}
          >
            <Upload size={28} color="#11ff99" style={{ margin:"0 auto 12px" }}/>
            <div style={{ fontFamily:"var(--font-ui)", fontSize:14, fontWeight:500, color:"var(--ink)", marginBottom:4 }}>
              {dragging ? "Drop your CSV here" : "Drag & drop a CSV file"}
            </div>
            <div style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--muted)" }}>or click to browse</div>
            <input ref={fileInput} type="file" accept=".csv,text/csv" onChange={onFile} style={{ display:"none" }}/>
          </div>
        </div>

        {/* Paste zone */}
        <div className="card">
          <div className="card-title">Paste CSV text</div>
          <div style={{ display:"flex", gap:8, marginBottom:10 }}>
            <input
              value={pasteSymbol} onChange={e=>setPasteSymbol(e.target.value)}
              placeholder="Symbol (e.g. AAPL) — for OHLCV pastes"
              className="input"
              style={{ fontFamily:"var(--font-mono)", fontSize:11, padding:"7px 10px", flex:1 }}
            />
          </div>
          <textarea
            value={pasteText} onChange={e=>setPasteText(e.target.value)}
            rows={6} className="input"
            placeholder='time,open,high,low,close,volume&#10;2024-01-02,370.5,372.1,369.8,371.6,75000000&#10;…'
            style={{ fontFamily:"var(--font-mono)", fontSize:11, resize:"vertical", marginBottom:10, width:"100%" }}
          />
          <button
            className="btn btn-primary btn-sm"
            onClick={()=>pasteText.trim() && parseAndUpload(pasteText)}
            disabled={!pasteText.trim() || parsing}
            style={{ opacity: (!pasteText.trim()||parsing)?0.5:1 }}
          >
            {parsing ? <><RefreshCw size={11} style={{animation:"spin 1s linear infinite"}}/> Parsing…</> : <><Check size={11}/> Parse & upload</>}
          </button>
        </div>
      </div>

      {/* Result banner */}
      {result && (
        <div className="card" style={{ border:`1px solid ${result.ok?"rgba(17,255,153,0.2)":"rgba(248,113,113,0.2)"}`, background: result.ok ? "rgba(17,255,153,0.04)" : "rgba(248,113,113,0.04)" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background: result.ok ? "var(--green)" : "var(--red)" }}/>
            <span style={{ fontFamily:"var(--font-ui)", fontSize:13, fontWeight:600, color:"var(--ink)" }}>
              {result.ok
                ? (result.format === 'ohlcv'
                    ? `Cached ${result.count} OHLCV bars as "${result.symbol}" (${result.range})`
                    : `Uploaded ${result.count} trades to test_table`)
                : "Upload failed"}
            </span>
          </div>
          {result.err && (
            <div style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--red)", paddingLeft:16 }}>{result.err}</div>
          )}
          {result.ok && result.format === 'ohlcv' && (
            <div style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--muted)", paddingLeft:16 }}>
              Open the Backtest page — the symbol picker now includes "{result.symbol}".
            </div>
          )}
          {result.ok && result.format === 'tradingview' && (
            <div style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--muted)", paddingLeft:16 }}>
              Open the Tearsheet to see your real performance data.
            </div>
          )}
        </div>
      )}

      {/* Cached symbols list */}
      {cached.length > 0 && (
        <div className="card">
          <div className="card-title" style={{display:"flex",justifyContent:"space-between"}}>
            <span>Cached OHLCV symbols</span>
            <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--muted)"}}>{cached.length} loaded</span>
          </div>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:6 }}>
            {cached.map(sym => (
              <span key={sym} style={{
                fontFamily:"var(--font-mono)", fontSize:11, padding:"3px 10px",
                background:"rgba(59,158,255,0.08)", border:"1px solid rgba(59,158,255,0.2)",
                borderRadius:"var(--r-full)", color:"#3b9eff",
              }}>{sym}</span>
            ))}
          </div>
        </div>
      )}

      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

window.SettingsPage = SettingsPage;
